import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';

import { ApiService } from '@app/core/http/api.service';

import { Subject ,  Observable ,  timer } from 'rxjs';
import { map, takeUntil, switchMap, shareReplay } from 'rxjs/operators';

import { IGraphUser } from '../models';

@Injectable()
export class EmployeesService extends ApiService {

    // Cache refresh timer (a half hour)
    private readonly refreshInterval = 1800 * 1000;

    private employeesUrl: string;
    private employeesCache$: Observable<IGraphUser[]>;
    private stop$ = new Subject<void>();

    private options = {
        headers: this.defaultHeaders,
        observe: 'body' as 'body',
    };

    constructor(httpClient: HttpClient) {
        super(httpClient);

        if (!environment.api.endpoint) {
            throw new Error('Could not initialize the "employees" service.');
        }

        this.employeesUrl = `https://graph.microsoft.com/beta/users/`;
    }

    public getEmployeesStartWith(searchFilter: string): Observable<IGraphUser[]> {
        return this.httpClient.get<{ value: IGraphUser[] }>(
            `${this.employeesUrl}?$filter=startswith(userPrincipalName, '${searchFilter}') or
                                    startswith(displayName, '${searchFilter}') or
                                    startswith(givenName, '${searchFilter}') or
                                    startswith(surname, '${searchFilter}')`, this.options)
            .pipe(
                map(result => result.value),
            );
    }

    public getEmployee(id: string): Observable<IGraphUser> {
        return this.httpClient.get<{ value: IGraphUser }>(`${this.employeesUrl}/${id}'`, this.options).pipe(
            map(result => result.value),
        );
    }

    public get employees(): Observable<IGraphUser[]> {
        if (!this.employeesCache$) {
            // Set up timer that ticks every X milliseconds
            const timer$ = timer(0, this.refreshInterval);

            this.employeesCache$ = timer$.pipe(
                takeUntil(this.stop$),
                switchMap(() => this.getAll()),
                shareReplay<IGraphUser[]>(),
            );
        }

        return this.employeesCache$;
    }

    /**
     * Get the list of available employees
     * @param reportProgress Whether this request should be made in a way that exposes progress events.
     * @returns Observable of T. T depends on the 'observe' parameter.
     */
    private getAll(reportProgress = false): Observable<IGraphUser[]> {
        const options = {
            headers: this.defaultHeaders,
            observe: 'body' as 'body',
            reportProgress,
        };

        return this.httpClient.get<{ value: IGraphUser[] }>(this.employeesUrl, options).pipe(
            map(result => result.value),
        );
    }
}
